/**
 * The widget's one touch target.
 *
 * Web gets `:hover` and `:disabled` from CSS for free. RN has neither, so the
 * feedback the whole widget relies on — dim on press, dim harder when disabled,
 * never fire while disabled — has to be written once and reused, or every
 * component invents a slightly different one.
 *
 * `hitSlop` is not a nicety here: several of these are 18–28dp icon buttons
 * (mute, minimize, close) that sit below the 44dp touch target both platforms'
 * accessibility guidelines ask for. Expanding the touch area keeps the visual
 * design identical to web while making the buttons reachable.
 */

import type { FC, ReactNode } from "react";
import { Pressable, StyleSheet, type StyleProp, type ViewStyle } from "react-native";

export interface TouchableProps {
  onPress?: () => void;
  /** For press-driven animation; the opacity feedback is handled internally. */
  onPressIn?: () => void;
  onPressOut?: () => void;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
  /** `data-ringg` equivalent — pass the bare name, `ringgId` adds the prefix. */
  testID?: string;
  accessibilityLabel?: string;
  /** Extra touch area beyond the visual bounds, in dp. Defaults to 8. */
  hitSlop?: number;
  children?: ReactNode;
}

/** Matches the web widget's `hover:opacity-90` / `disabled:opacity-50`. */
const PRESSED_OPACITY = 0.9;
const DISABLED_OPACITY = 0.5;

const DEFAULT_HIT_SLOP = 8;

export const Touchable: FC<TouchableProps> = ({ onPress, onPressIn, onPressOut, disabled = false, style, testID, accessibilityLabel, hitSlop = DEFAULT_HIT_SLOP, children }) => (
  <Pressable
    testID={testID}
    accessibilityRole="button"
    accessibilityLabel={accessibilityLabel}
    accessibilityState={{ disabled }}
    // `disabled` alone stops the press; the opacity below states it visually.
    disabled={disabled}
    onPress={onPress}
    onPressIn={onPressIn}
    onPressOut={onPressOut}
    hitSlop={hitSlop}
    style={({ pressed }) => [style, disabled ? styles.disabled : pressed ? styles.pressed : null]}
  >
    {children}
  </Pressable>
);

const styles = StyleSheet.create({
  pressed: { opacity: PRESSED_OPACITY },
  disabled: { opacity: DISABLED_OPACITY },
});
